import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import icHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import IcTagSvg from '../../../assets/images/svg/ic-tag.svg';
import AutoCompletedTextField from '../../shared/AutoCompletedTextField';
import AutocompletePanel from '../../shared/AutocompletePanel';
import Checkbox from '../../shared/Checkbox';
import Dropdown from '../../shared/Dropdown';
import TextEditor from '../../shared/TextEditor';
import TextFieldCardCounter from '../../shared/TextFieldCardCounter';
import TextFieldSearch from '../../shared/TextFieldSearch';
import Tooltip from '../../shared/Tooltip';

const useStyles = makeStyles(
  () => ({
    root: {
      padding: '16px',
      borderRadius: 24,
      backgroundColor: color.$23,
      width: 633,
      marginBottom: 36,
    },
    title: {
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: 'normal',
      color: color.$3,
      marginBottom: 8,
    },
    tagContainer: {
      display: 'flex',
      alignItems: 'start',
      margin: '8px 0',
      '& > img': {
        display: 'block',
        width: 24,
        height: 24,
        margin: '10px 6px 0 0',
      },
      '& > div': {
        padding: '12px 16px',
        width: '100%',
      },
      '& textarea': {
        height: 20,
        minHeight: 20,
      },
      '& textarea::placeholder': {
        fontSize: '15px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        wordBreak: 'break-all',
      },
    },
    textField: {
      display: 'block',
      flex: '1 1 auto',
      width: '100%',
      height: 44,
      fontSize: 15,
      lineHeight: '21px',
      outline: 'none',
      padding: '0 16px',
      borderRadius: 22,
      border: `1px solid ${color.$10}`,
      backgroundColor: color.$7,
      '&:focus': {
        borderColor: color.$1,
      },
    },
    dropdown: {
      width: 140,
    },
    customer: {
      position: 'relative',
      width: '100%',
      height: '44px',
    },
    menuContainer: {
      maxHeight: '192px',
      overflowY: 'auto',
      padding: '8px 0',
      borderRadius: '0px 0px 12px 12px',
    },
    customerRow: {
      position: 'relative',
      padding: '6px 24px',
      margin: 0,
    },
    paperClass: {
      width: 300,
    },
    button1Block: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 8,
    },
    rejectReplyLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 15,
      lineHeight: 1,
      color: color.$13,
      '& > img': {
        marginLeft: 4,
      },
    },
    reflectModeLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 13,
      lineHeight: 1,
      color: color.$13,
      '& > img': {
        marginLeft: 4,
      },
    },
  }),
  {
    name: 'ReplyButton',
  },
);

function ReplyButton({
  reflectMode,
  rejectReply,
  onRejectReplyChange,
  buttons,
  candidateTags,
  members = [],
  botList = [],
  onChange,
  onButtonChange,
  onTagChange,
  onMemberChange,
  isSaved,
  isLoading,
  disabled,
  onCheckInsightArkProduct,
}) {
  const [searchCustomerTerm, setSearchCustomerTerm] = useState('');
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const { pathKey } = useSelector(store => store.aiBot);

  const customerServiceItems = [
    {
      text: t(`${pathKey}:unassigned`),
      value: 'unassigned',
    },
    {
      text: t(`${pathKey}:assigned`),
      value: 'private',
    },
  ];

  const handleChangeSearchCustomerTerm = v => {
    if (!v) {
      onButtonChange(
        buttons.map((b, i) => {
          if (i === 1) {
            return {
              ...b,
              displayName: '',
              userId: '',
            };
          }
          return b;
        }),
      );
    }
    setSearchCustomerTerm(v);
    onMemberChange(v);
  };

  const handleCustomerClick = customer => {
    setSearchCustomerTerm('');
    setIsCustomerOpen(false);
    onButtonChange(
      buttons.map((b, i) => {
        if (i === 1) {
          return {
            ...b,
            displayName: customer?.displayName,
            userId: customer?.objectId,
          };
        }
        return b;
      }),
    );
  };

  return (
    <>
      <div style={{ marginBottom: 0 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={reflectMode?.enabled}
              onChange={() => {
                onChange({
                  reflectMode: {
                    enabled: !reflectMode?.enabled,
                  },
                });
              }}
              name="reflectMode"
              color="primary"
            />
          }
          label={
            <div className={classes.reflectModeLabel}>
              {t(`${pathKey}:reflectMode`)}
              <Tooltip
                light
                placement="top"
                title={t(`${pathKey}:reflectModeTooltip`)}
              >
                <img src={icHelpCircle} alt="reflectMode tooltip" />
              </Tooltip>
            </div>
          }
        />
      </div>
      <div className={classes.root}>
        <div className={classes.title}>
          {t(`${pathKey}:triggerSettingContainer.endingCardButton`)}
        </div>
        <TextFieldCardCounter
          style={{ width: 226 }}
          placeholder={t(
            `${pathKey}:triggerSettingContainer.endingCardButtonMessage`,
          )}
          maxCount={0}
          value={buttons?.[0]?.title || ''}
          onChange={v =>
            onButtonChange(
              buttons.map((b, i) => {
                if (i === 0) {
                  return {
                    ...b,
                    title: v,
                    text: v,
                  };
                }
                return b;
              }),
            )
          }
          status={isSaved && !buttons?.[0]?.title ? 'error' : 'default'}
          isDisable={disabled}
        />
        <div className={classes.tagContainer}>
          <img src={IcTagSvg} alt="Tag" />
          <AutoCompletedTextField
            placeholder={t(
              `${pathKey}:triggerSettingContainer.endingCardButtonTags`,
            )}
            candidates={candidateTags || []}
            defaultTags={buttons?.[0]?.tags || []}
            type="inline"
            onEnterEvent={tagName => {
              onCheckInsightArkProduct(() => {
                onButtonChange(
                  buttons.map((b, i) => {
                    if (i === 0) {
                      return {
                        ...b,
                        tags: [...b?.tags, tagName],
                      };
                    }
                    return b;
                  }),
                );
              });
            }}
            onSetTags={tagNames => {
              onCheckInsightArkProduct(() => {
                onButtonChange(
                  buttons.map((b, i) => {
                    if (i === 0) {
                      return {
                        ...b,
                        tags: tagNames,
                      };
                    }
                    return b;
                  }),
                );
              });
            }}
            onChange={onTagChange}
            isLoading={isLoading}
            disabled={disabled}
            multiline
          />
        </div>
        <div className={classes.title}>
          {t(
            `${pathKey}:triggerSettingContainer.endingCardButtonCustomerService`,
          )}
        </div>
        <div className={classes.button1Block}>
          <TextFieldCardCounter
            style={{ width: 226 }}
            placeholder={t(
              `${pathKey}:triggerSettingContainer.endingCardButtonMessage`,
            )}
            maxCount={0}
            value={buttons?.[1]?.title || ''}
            onChange={v =>
              onButtonChange(
                buttons.map((b, i) => {
                  if (i === 1) {
                    return {
                      ...b,
                      title: v,
                      text: v,
                    };
                  }
                  return b;
                }),
              )
            }
            status={isSaved && !buttons?.[1]?.title ? 'error' : 'default'}
            isDisable={disabled}
          />
        </div>
        <div className={classes.button1Block}>
          <Dropdown
            isDialog
            style={{ width: 226, minWidth: 226 }}
            menuItems={customerServiceItems}
            value={buttons?.[1]?.inbox}
            onChange={v => {
              onCheckInsightArkProduct(() => {
                onButtonChange(
                  buttons.map((b, i) => {
                    if (i === 1) {
                      return {
                        ...b,
                        inbox: v,
                        displayName: '',
                        userId: '',
                      };
                    }
                    return b;
                  }),
                );
              });
            }}
            disabled={disabled}
          />
          {buttons?.[1]?.inbox === 'private' && (
            <ClickAwayListener
              onClickAway={() => {
                setIsCustomerOpen(false);
                setSearchCustomerTerm('');
              }}
            >
              <div className={classes.customer}>
                <TextFieldSearch
                  value={searchCustomerTerm || buttons?.[1]?.displayName || ''}
                  placeholder={t(
                    `${pathKey}:triggerSettingContainer.endingCardButtonCustomerServiceDropdown`,
                  )}
                  onChange={handleChangeSearchCustomerTerm}
                  onFocus={() => setIsCustomerOpen(true)}
                  disabled={disabled}
                  showClearButton={false}
                  status={
                    isSaved && !buttons?.[1]?.userId ? 'error' : 'default'
                  }
                />
                {!disabled && isCustomerOpen && members.length > 0 && (
                  <AutocompletePanel
                    paperClass={classes.paperClass}
                    menuContainerClass={classes.menuContainer}
                    body1Class={classes.customerRow}
                    dataArray={members}
                    value="displayName"
                    extraContent=""
                    extraPicture
                    handleClick={handleCustomerClick}
                    popup
                  />
                )}
              </div>
            </ClickAwayListener>
          )}
        </div>
        {botList?.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!buttons?.[1]?.flow}
                  onChange={() => {
                    onCheckInsightArkProduct(() => {
                      onButtonChange(
                        buttons.map((b, i) => {
                          if (i === 1) {
                            return {
                              ...b,
                              flow: buttons?.[1]?.flow
                                ? undefined
                                : botList?.[0]?.value,
                            };
                          }
                          return b;
                        }),
                      );
                    });
                  }}
                  name="rejectReply"
                  color="primary"
                />
              }
              label={
                <div className={classes.rejectReplyLabel}>
                  {t(`${pathKey}:triggerBot`)}
                  <Tooltip
                    light
                    placement="top"
                    title={t(`${pathKey}:triggerBotTooltip`)}
                  >
                    <img src={icHelpCircle} alt="rejectReply tooltip" />
                  </Tooltip>
                </div>
              }
              disabled={disabled}
            />
            {buttons?.[1]?.flow && (
              <Dropdown
                isDialog
                style={{ width: '100%' }}
                menuItems={botList}
                value={buttons?.[1]?.flow}
                onChange={v => {
                  onCheckInsightArkProduct(() => {
                    onButtonChange(
                      buttons.map((b, i) => {
                        if (i === 1) {
                          return {
                            ...b,
                            flow: v,
                          };
                        }
                        return b;
                      }),
                    );
                  });
                }}
                disabled={disabled}
              />
            )}
          </div>
        )}
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={rejectReply?.enabled}
                onChange={() => {
                  onRejectReplyChange({ enabled: !rejectReply?.enabled });
                }}
                name="rejectReply"
                color="primary"
              />
            }
            label={
              <div className={classes.rejectReplyLabel}>
                {t(`${pathKey}:rejectReply`)}
                <Tooltip
                  light
                  placement="top"
                  title={t(`${pathKey}:rejectReplyTooltip`)}
                >
                  <img src={icHelpCircle} alt="rejectReply tooltip" />
                </Tooltip>
              </div>
            }
          />
          <TextEditor
            onChange={v => {
              onRejectReplyChange({ message: v });
            }}
            origriData={rejectReply?.message}
            placeholder={t(`${pathKey}:rejectReplyPlaceholder`)}
            style={{ width: '100%' }}
            isError={isSaved && rejectReply?.enabled && !rejectReply?.message}
          />
        </div>
        <div className={classes.tagContainer}>
          <img src={IcTagSvg} alt="Tag" />
          <AutoCompletedTextField
            placeholder={t(
              `${pathKey}:triggerSettingContainer.endingCardButtonTags`,
            )}
            candidates={candidateTags || []}
            defaultTags={buttons?.[1]?.tags || []}
            type="inline"
            onEnterEvent={tagName => {
              onCheckInsightArkProduct(() => {
                onButtonChange(
                  buttons.map((b, i) => {
                    if (i === 1) {
                      return {
                        ...b,
                        tags: [...b?.tags, tagName],
                      };
                    }
                    return b;
                  }),
                );
              });
            }}
            onSetTags={tagNames => {
              onCheckInsightArkProduct(() => {
                onButtonChange(
                  buttons.map((b, i) => {
                    if (i === 1) {
                      return {
                        ...b,
                        tags: tagNames,
                      };
                    }
                    return b;
                  }),
                );
              });
            }}
            onChange={onTagChange}
            isLoading={isLoading}
            disabled={disabled}
            multiline
          />
        </div>
      </div>
    </>
  );
}

export default ReplyButton;
